/**
 * Custom hook for saving linkedTable changes using TanStack Query mutation
 * Replaces useLinkedTableSave with better cache invalidation
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import type { LinkedTableCalculatedColumnApi } from '@loader/shared-types';

import {
  isFilterComplete,
  isCalculatedColumnComplete,
  isRelationshipComplete,
} from '@/features/linkedTables/editLinkedTable/hooks/useLinkedTableEditor';
import { mapFieldMappingToCondition } from '@/features/linkedTables/editLinkedTable/utils/joinConditionMapper';
import { useRpc } from '@/lib/rpc';
import { logger } from '@/lib/logger';
import { toast } from '@/lib/toast';
import { queryKeys } from '@/lib/queryKeys';
import type {
  LinkedTable,
  LinkedTableFilter,
  LinkedTableCalculatedColumn,
  LinkedTableRelationship,
  LinkedTableValue,
  LinkedTableRowGroupSort,
  LinkedTableVariable,
} from '@/lib/types';

/**
 * Convert LinkedTableCalculatedColumn (local format with `formula` property)
 * to LinkedTableCalculatedColumnApi (API format without `formula` property)
 */
function toApiCalculatedColumn(column: LinkedTableCalculatedColumn): LinkedTableCalculatedColumnApi {
  // Ensure data_type is one of the allowed API types
  const allowedTypes: readonly string[] = ['text', 'number', 'boolean', 'date', 'datetime'];
  const data_type = column.data_type && allowedTypes.includes(column.data_type)
    ? column.data_type as LinkedTableCalculatedColumnApi['data_type']
    : undefined;

  return {
    id: column.id,
    name: column.name,
    formula_id: column.formula_id,
    formula_value: column.formula_value,
    description: column.description,
    data_type,
    dependencies: column.dependencies,
  };
}

interface UseLinkedTableSaveMutationProps {
  isCreateMode: boolean;
  linkedTable: LinkedTable | null;
  linkedTableName: string;
  linkedTableDescription: string;
  filters: LinkedTableFilter[];
  calculated_columns: LinkedTableCalculatedColumn[];
  relationships: LinkedTableRelationship[];
  row_groups: string[];
  row_group_sorts: LinkedTableRowGroupSort[];
  values: LinkedTableValue[];
  variables: LinkedTableVariable[];
  setLinkedTable: (linkedTable: LinkedTable) => void;
  syncOriginalState: () => void;
}

interface UseLinkedTableSaveMutationResult {
  handleSave: () => Promise<void>;
  isSaving: boolean;
}

/**
 * Hook for saving linkedTable changes using TanStack Query mutation
 * Automatically invalidates related queries on success
 */
export const useLinkedTableSaveMutation = ({
  isCreateMode,
  linkedTable,
  linkedTableName,
  linkedTableDescription,
  filters,
  calculated_columns,
  relationships,
  row_groups,
  row_group_sorts,
  values,
  variables,
  setLinkedTable,
  syncOriginalState,
}: UseLinkedTableSaveMutationProps): UseLinkedTableSaveMutationResult => {
  const rpc = useRpc();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const mutation = useMutation({
    mutationFn: async (): Promise<LinkedTable> => {
      // Validate completeness before saving
      const incompleteFilters = filters.filter((f) => !isFilterComplete(f));
      const incompleteCalculatedColumns = calculated_columns.filter(
        (c) => !isCalculatedColumnComplete(c)
      );
      const incompleteRelationships = relationships.filter(
        (r) => !isRelationshipComplete(r)
      );

      if (
        incompleteFilters.length > 0 ||
        incompleteCalculatedColumns.length > 0 ||
        incompleteRelationships.length > 0
      ) {
        const errors: string[] = [];
        if (incompleteFilters.length > 0) {
          errors.push(
            `${incompleteFilters.length} incomplete filter${incompleteFilters.length > 1 ? 's' : ''}`
          );
        }
        if (incompleteCalculatedColumns.length > 0) {
          errors.push(
            `${incompleteCalculatedColumns.length} incomplete calculated column${incompleteCalculatedColumns.length > 1 ? 's' : ''}`
          );
        }
        if (incompleteRelationships.length > 0) {
          errors.push(
            `${incompleteRelationships.length} incomplete relationship${incompleteRelationships.length > 1 ? 's' : ''}`
          );
        }
        throw new Error(
          `Cannot save: ${errors.join(', ')}. Please complete all items before saving.`
        );
      }

      if (isCreateMode) {
        logger.info('Creating new linkedTable');

        // For create mode, build SQL-native tables and joins from linkedTable state
        const tables = [];
        const joins = [];

        // Always include the base table from the existing linkedTable
        if (linkedTable && linkedTable.tables && linkedTable.tables.length > 0) {
          // Version selection not stored in table definition (resolved at query time)
          tables.push(linkedTable.tables[0]);

          // Convert relationships to tables and joins
          for (const rel of relationships.filter(isRelationshipComplete)) {
            // Add the joined table (version selection via query parameters)
            tables.push({
              id: rel.id,
              type: rel.target_type,
              target_id: rel.target_id,
              alias: rel.alias,
            });

            // Add the join
            joins.push({
              id: rel.id,
              left_table: linkedTable.tables[0].alias,
              right_table: rel.alias,
              join_type: (rel.join_type || 'left').toUpperCase() as 'LEFT' | 'INNER' | 'RIGHT' | 'FULL',
              conditions: (rel.field_mappings || []).map(mapFieldMappingToCondition),
            });
          }
        }

        const res = await rpc.api.private['linked-tables'].$post({
          json: {
            name: linkedTableName,
            description: linkedTableDescription,
            tables,
            joins: joins.length > 0 ? joins : null,
            filters: filters.filter(isFilterComplete),
            calculated_columns: calculated_columns
              .filter(isCalculatedColumnComplete)
              .map(toApiCalculatedColumn),
            row_groups: row_groups.length > 0 ? row_groups : null,
            row_group_sorts: row_group_sorts.length > 0 ? row_group_sorts : null,
            values: values.length > 0 ? values : null,
            variables: variables.length > 0 ? variables : null,
          }
        });
        if (!res.ok) throw new Error('Failed to create linkedTable');
        const response = await res.json();
        return response.linkedTable as unknown as LinkedTable;
      } else if (linkedTable) {
        logger.info('Updating linkedTable', { linkedTableId: linkedTable.id });
        // Only save complete items
        const validFilters = filters.filter(isFilterComplete);
        const validCalculatedColumns = calculated_columns.filter(
          isCalculatedColumnComplete
        );
        const validRelationships = relationships.filter(isRelationshipComplete);

        // Convert frontend relationships to SQL-native tables + joins format
        const tables = [];
        const joins = [];

        // Always include the base table from the existing linkedTable
        if (linkedTable.tables && linkedTable.tables.length > 0) {
          // Version selection not stored in table definition (resolved at query time)
          tables.push(linkedTable.tables[0]);

          // Convert relationships to tables and joins
          for (const rel of validRelationships) {
            // Add the joined table (version selection via query parameters)
            tables.push({
              id: rel.id,
              type: rel.target_type,
              target_id: rel.target_id,
              alias: rel.alias,
            });

            // Add the join
            joins.push({
              id: rel.id,
              left_table: linkedTable.tables[0].alias,
              right_table: rel.alias,
              join_type: (rel.join_type || 'left').toUpperCase() as 'LEFT' | 'INNER' | 'RIGHT' | 'FULL',
              conditions: (rel.field_mappings || []).map(mapFieldMappingToCondition),
            });
          }
        }

        const updatePayload = {
          name: linkedTableName,
          description: linkedTableDescription,
          filters: validFilters,
          calculated_columns: validCalculatedColumns.map(toApiCalculatedColumn),
          tables,
          joins: joins.length > 0 ? joins : null,
          row_groups: row_groups.length > 0 ? row_groups : null,
          row_group_sorts: row_group_sorts.length > 0 ? row_group_sorts : null,
          values: values.length > 0 ? values : null,
          variables: variables.length > 0 ? variables : null,
        };

        // @ts-expect-error - Hono RPC type inference limitation
        const res = await rpc.api.private['linked-tables'][':id'].$put({
          param: { id: linkedTable.id },
          json: updatePayload
        });
        if (!res.ok) throw new Error('Failed to update linkedTable');
        const response = await res.json();
        return response.linkedTable as unknown as LinkedTable;
      } else {
        throw new Error('No linkedTable to save');
      }
    },
    // Retry strategy: don't retry mutations (they're not idempotent)
    retry: false,
    // Optimistic updates could be added here for better UX
    // onMutate: async (variables) => { ... },
    onSuccess: (savedLinkedTable) => {
      logger.info('LinkedTable saved successfully', { linkedTableId: savedLinkedTable.id });

      if (isCreateMode) {
        toast.linkedTable.created();
        // Navigate back to workspace, staying in current folder if applicable
        const folderId = savedLinkedTable.folder_id;
        void navigate({
          to: folderId ? '/workspace/folder/$id' : '/workspace',
          params: folderId ? { id: folderId } : undefined,
        });
      } else {
        toast.linkedTable.saved();
        // Update the linkedTable reference (for metadata like id, timestamps)
        setLinkedTable(savedLinkedTable);
        // Sync original state for change detection
        // Don't reinitialize the full state - keep current UI state intact
        syncOriginalState();

        // Don't update React Query cache or invalidate the current linkedTable query
        // This prevents triggering useLinkedTableInitialization which would overwrite our state
        // The cache will naturally update on next page load

        // Invalidate related queries (but NOT the current linkedTable!)
        // Invalidate prelinkedTable data to refresh with new filters
        void queryClient.invalidateQueries({
          queryKey: ['linkedTable-prelinkedTable', savedLinkedTable.id],
          refetchType: 'active',
        });
        // Invalidate linkedTables list to update any list views (including Links page)
        void queryClient.invalidateQueries({
          queryKey: queryKeys.linkedTables.all,
          exact: false,
          refetchType: 'active',
        });
        // Note: We don't invalidate table queries because:
        // 1. Saving a linkedTable doesn't change the table schema or fields
        // 2. Invalidating causes tableFields to refetch, making availableFields temporarily empty
        // 3. This causes selectedField lookup to fail, making field names disappear during refetch
      }
    },
    onError: (error) => {
      logger.error('Error saving linkedTable', error, {
        isCreateMode,
        linkedTableId: linkedTable?.id,
      });
      if (isCreateMode) {
        toast.linkedTable.createdFailed(error instanceof Error ? error.message : undefined);
      } else {
        toast.linkedTable.savedFailed(error instanceof Error ? error.message : undefined);
      }
    },
  });

  return {
    handleSave: async () => {
      await mutation.mutateAsync();
    },
    isSaving: mutation.isPending,
  };
};
